import { useEffect, useState } from "react";
import { useSearchParams } from "react-router-dom";
import axios from "axios";
import { ShieldCheck, XCircle, Search, Loader2 } from "lucide-react";
import { Header } from "../components/Header";
import { Footer } from "../components/Footer";
import { PremiumBadge, PremiumFrame } from "../components/vcb/PremiumBadge";

const API = process.env.REACT_APP_BACKEND_URL;

export default function VerifyReceipt() {
  const [params] = useSearchParams();
  const [hash, setHash] = useState(params.get("hash") || "");
  const [busy, setBusy] = useState(false);
  const [res, setRes] = useState(null);
  const [err, setErr] = useState("");

  const verify = async (h) => {
    const v = (h ?? hash).trim();
    if (!v) return;
    setBusy(true); setErr(""); setRes(null);
    try { setRes((await axios.get(`${API}/api/vcb/receipts/verify`, { params: { hash: v } })).data); }
    catch (e) { setErr(e.response?.data?.detail || "Verification service unavailable"); }
    finally { setBusy(false); }
  };
  useEffect(() => { if (params.get("hash")) verify(params.get("hash")); }, []); // eslint-disable-line react-hooks/exhaustive-deps

  return (
    <div className="min-h-screen bg-[#050505] text-[#F5F5F0]" data-testid="verify-receipt-page">
      <Header />
      <div className="max-w-3xl mx-auto px-6 py-16">
        <PremiumFrame className="p-10" testid="verify-receipt-frame">
          <div className="flex items-center gap-3"><PremiumBadge /><span className="font-grotesk text-[9px] uppercase tracking-[0.32em] text-[#8A8A82]">Digital Notary · Receipt Verification</span></div>
          <h1 className="font-serif text-4xl sm:text-5xl mt-6 leading-tight">Verify a VCB <span className="text-[#D8B23E]">Receipt</span></h1>
          <p className="font-body text-base text-[#C9C9C2] mt-4 max-w-2xl">Paste the SHA-256 document hash printed in the footer of any Vinci Core Banking phase receipt. A genuine receipt resolves to its sealed record below.</p>
          <div className="mt-8 flex gap-2">
            <input data-testid="verify-hash-input" value={hash} onChange={(e) => setHash(e.target.value)} onKeyDown={(e) => e.key === "Enter" && verify()}
                   placeholder="64-character SHA-256 hash" className="flex-1 bg-black/60 border border-white/15 px-4 py-3 font-mono text-sm focus:border-[#D8B23E] outline-none" />
            <button onClick={() => verify()} disabled={busy} data-testid="verify-hash-btn" className="inline-flex items-center gap-2 bg-[#D8B23E] text-black px-6 font-grotesk text-[9px] uppercase tracking-[0.28em] hover:bg-[#E5C450] disabled:opacity-60">{busy ? <Loader2 size={12} className="animate-spin" /> : <Search size={12} />} Verify</button>
          </div>
          {err && <div className="mt-4 text-[#E5484D] text-sm" data-testid="verify-error">{err}</div>}
          {res && (res.valid ? (
            <div className="mt-8 border border-[#2FB999]/60 bg-black/40 p-6" data-testid="verify-result-valid">
              <div className="inline-flex items-center gap-2 text-[#2FB999] font-grotesk text-[9px] uppercase tracking-[0.28em]"><ShieldCheck size={14} /> Genuine · sealed by {res.issued_by}</div>
              <dl className="grid sm:grid-cols-2 gap-x-6 gap-y-3 mt-5 text-sm">
                {[["Receipt №", res.receipt_no], ["Phase", `Phase ${res.phase} — ${res.phase_label}`], ["Base", `₹${Number(res.base_amount).toLocaleString("en-IN")}`], ["GST @ 18%", `₹${Number(res.gst_amount).toLocaleString("en-IN")}`], ["Total received", `₹${Number(res.total_amount).toLocaleString("en-IN")}`], ["Paid at", `${res.paid_at_ist} IST`], ["Member", `${res.partner_name || ""} ${res.partner}`], ["Matched", res.matched === "content" ? "Document content hash" : "PDF file hash"]].map(([k, v]) => (
                  <div key={k}><dt className="font-grotesk text-[8px] uppercase tracking-[0.26em] text-[#8A8A82]">{k}</dt><dd className="text-[#F5F5F0] mt-0.5">{v}</dd></div>))}
              </dl>
            </div>
          ) : (
            <div className="mt-8 border border-[#E5484D]/60 bg-black/40 p-6 inline-flex items-center gap-2 text-[#E5484D] font-grotesk text-[9px] uppercase tracking-[0.28em]" data-testid="verify-result-invalid"><XCircle size={14} /> No sealed receipt matches this hash</div>
          ))}
        </PremiumFrame>
      </div>
      <Footer />
    </div>
  );
}
